import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  Select,
  Button,
  Space,
  Alert,
  Tag,
  List,
  Divider,
  Spin,
  message
} from 'antd';
import {
  DiffOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import { animations } from '../styles/animations';
import { theme } from '../styles/theme';
import axios from 'axios';
import API_URL from '../config/api';

const { Title, Paragraph, Text } = Typography;

const ComparePage = () => {
  const [documents, setDocuments] = useState([]);
  const [doc1, setDoc1] = useState(null);
  const [doc2, setDoc2] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/documents`);
      setDocuments(response.data.documents || []);
    } catch (error) {
      message.error('Failed to load documents');
    }
  };

  const handleCompare = async () => {
    if (!doc1 || !doc2) {
      message.warning('Please select two documents to compare');
      return;
    }

    if (doc1 === doc2) {
      message.warning('Please select two different documents');
      return;
    }

    setLoading(true);
    try {
      console.log('Comparing documents:', { docId1: doc1, docId2: doc2 });
      const response = await axios.post(`${API_URL}/api/documents/compare`, {
        docId1: doc1,
        docId2: doc2
      });
      console.log('Comparison response:', response.data);
      setComparison(response.data.comparison);
      message.success('Comparison complete!');
    } catch (error) {
      console.error('Comparison error:', error);
      console.error('Error response:', error.response?.data);
      message.error(`Comparison failed: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getImpactColor = (impact) => {
    switch (impact?.toLowerCase()) {
      case 'high':
        return theme.colors.impactHigh;
      case 'medium':
        return theme.colors.impactMedium;
      case 'low':
        return theme.colors.impactLow;
      default:
        return theme.colors.textSecondary;
    }
  };

  const getImpactTag = (impact) => {
    const color = impact?.toLowerCase() === 'high' ? 'red' :
                  impact?.toLowerCase() === 'medium' ? 'orange' : 'green';
    return <Tag color={color}>{impact} Impact</Tag>;
  };

  return (
    <motion.div {...animations.fadeIn}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <DiffOutlined /> Compare Documents
        </Title>
        <Paragraph type="secondary">
          Identify key changes, additions, and deletions between two versions of a loan agreement.
        </Paragraph>
      </div>

      {/* Document Selection */}
      <Card style={{ marginBottom: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Document 1 (Original)
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select first document"
              value={doc1}
              onChange={setDoc1}
              options={documents.map(doc => ({
                value: doc.id,
                label: (
                  <Space>
                    {doc.filename}
                    {doc.is_sample && <Tag color="blue">Sample</Tag>}
                  </Space>
                )
              }))}
            />
          </div>

          <div>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Document 2 (Revised)
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder="Select second document"
              value={doc2}
              onChange={setDoc2}
              options={documents.map(doc => ({
                value: doc.id,
                label: (
                  <Space>
                    {doc.filename}
                    {doc.is_sample && <Tag color="blue">Sample</Tag>}
                  </Space>
                )
              }))}
            />
          </div>

          <Button
            type="primary"
            size="large"
            icon={<DiffOutlined />}
            onClick={handleCompare}
            disabled={!doc1 || !doc2}
            loading={loading}
            block
          >
            Compare Documents
          </Button>
        </Space>
      </Card>

      {/* Loading State */}
      {loading && (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <Title level={4} style={{ marginTop: '16px' }}>
              Comparing documents...
            </Title>
            <Text type="secondary">AI is analyzing the differences</Text>
          </div>
        </Card>
      )}

      {/* Results */}
      {!loading && comparison && (
        <motion.div {...animations.slideUp}>
          <Alert
            message="Comparison Complete"
            description={comparison.summary}
            type="info"
            icon={<CheckCircleOutlined />}
            showIcon
            style={{ marginBottom: '24px' }}
          />

          {/* Key Changes */}
          <Card
            title={
              <Space>
                <WarningOutlined style={{ color: theme.colors.warning }} />
                <span>Key Changes</span>
              </Space>
            }
            style={{ marginBottom: '24px' }}
          >
            <List
              dataSource={comparison.keyChanges || []}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <Space>
                        <Text strong>{item.category}</Text>
                        {getImpactTag(item.impact)}
                      </Space>
                    }
                    description={item.change}
                  />
                </List.Item>
              )}
            />
          </Card>

          {/* Added Clauses */}
          {comparison.addedClauses && comparison.addedClauses.length > 0 && (
            <Card
              title={
                <Space>
                  <PlusCircleOutlined style={{ color: theme.colors.success }} />
                  <span>Added Clauses</span>
                  <Tag color="green">{comparison.addedClauses.length}</Tag>
                </Space>
              }
              style={{ marginBottom: '24px' }}
            >
              <List
                dataSource={comparison.addedClauses}
                renderItem={(item) => (
                  <List.Item>
                    <Text>
                      <CheckCircleOutlined
                        style={{ color: theme.colors.success, marginRight: '8px' }}
                      />
                      {item}
                    </Text>
                  </List.Item>
                )}
              />
            </Card>
          )}

          {/* Removed Clauses */}
          {comparison.removedClauses && comparison.removedClauses.length > 0 && (
            <Card
              title={
                <Space>
                  <MinusCircleOutlined style={{ color: theme.colors.error }} />
                  <span>Removed Clauses</span>
                  <Tag color="red">{comparison.removedClauses.length}</Tag>
                </Space>
              }
              style={{ marginBottom: '24px' }}
            >
              <List
                dataSource={comparison.removedClauses}
                renderItem={(item) => (
                  <List.Item>
                    <Text delete type="secondary">
                      {item}
                    </Text>
                  </List.Item>
                )}
              />
            </Card>
          )}

          {/* Modified Terms */}
          {comparison.modifiedTerms && comparison.modifiedTerms.length > 0 && (
            <Card
              title={
                <Space>
                  <DiffOutlined style={{ color: theme.colors.info }} />
                  <span>Modified Terms</span>
                  <Tag color="blue">{comparison.modifiedTerms.length}</Tag>
                </Space>
              }
            >
              <List
                dataSource={comparison.modifiedTerms}
                renderItem={(item) => (
                  <List.Item>
                    <Text>{item}</Text>
                  </List.Item>
                )}
              />
            </Card>
          )}
        </motion.div>
      )}
    </motion.div>
  );
};

export default ComparePage;
